import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, Checkbox, ComboBox, Detail, EMPHASIS, Heading, SubHeading, Text} from '@forecast-it/design-system';

import DirectApi from '../../../../directApi';
import {clearXeroLineConfigCache} from '../../../../containers/modal/export_invoice_modal/xeroLineConfigCache';
import {ConfigPageCard} from './XeroConfigPageStyled';

const Description = styled.div`
	max-width: 1080px;
	margin-top: ${props => props.theme.sys.spacing['300']};
`;

const OrganizationField = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.sys.spacing['200']};
	margin-top: ${props => props.theme.sys.spacing['600']};
	width: 280px;
	max-width: 100%;
`;

const Rows = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.sys.spacing['300']};
	margin-top: ${props => props.theme.sys.spacing['600']};
`;

// Checkbox and label, then the status beside them. The status (Detail noWrap) never breaks inside
// itself, so a date cannot split; on a narrow window the whole status moves under the label instead.
const Row = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	column-gap: ${props => props.theme.sys.spacing['300']};
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: ${props => props.theme.sys.spacing['300']};
	margin-top: ${props => props.theme.sys.spacing['600']};
`;

const ORGANIZATION_LABEL_ID = 'xero-config-organization-label';
const statusIdFor = configType => `xero-config-${configType.key}-status`;

/**
 * The configuration types offered on the page, one row each.
 *
 * Only types whose import endpoint exists belong here. Adding one is a single entry with its own
 * configGroup and path, and its row appears with no other change to this component - the status
 * endpoint returns every group rather than a named one, so a new type's rows come back without
 * touching graphql. All three entries below arrived that way.
 */
export const CONFIG_TYPES = [
	{
		key: 'ACCOUNTS_AND_TAX',
		configGroup: 'XERO_ACCOUNTS_AND_TAX',
		labelId: 'settings.app_catalog.xero.config_accounts_and_tax',
		importPath: 'xero/configuration/import/accounts-and-tax',
	},
	{
		key: 'PRODUCTS_AND_SERVICES',
		configGroup: 'XERO_PRODUCTS_AND_SERVICES',
		labelId: 'settings.app_catalog.xero.config_products_and_services',
		importPath: 'xero/configuration/import/products-and-services',
	},
	{
		key: 'TRACKING_CATEGORIES',
		configGroup: 'XERO_TRACKING_CATEGORIES',
		labelId: 'settings.app_catalog.xero.config_tracking_categories',
		importPath: 'xero/configuration/import/tracking-categories',
	},
];

const IMPORT_STATUS_PATH = 'xero/configuration/import-status';

const XeroConfigurationImport = () => {
	const {formatMessage, formatDate, formatTime} = useIntl();

	const [organisations, setOrganisations] = useState([]);
	const [selectedTenantId, setSelectedTenantId] = useState(null);
	const [statusRows, setStatusRows] = useState([]);
	const [statusUnavailable, setStatusUnavailable] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [runningKeys, setRunningKeys] = useState([]);
	// Keyed by {tenantId, key}, not by key alone: describe() checks failures before the only
	// organisation-scoped lookup, so a bare key would follow the dropdown onto a different
	// organisation and show its failure against another organisation's configuration.
	const [failures, setFailures] = useState([]);

	const loadStatus = useCallback(
		() =>
			DirectApi.Fetch_Raw(IMPORT_STATUS_PATH)
				.then(result => {
					if (result.status !== 200) {
						// A backend outage must not read as a clean never-imported state.
						setStatusUnavailable(true);
						return null;
					}
					return result.json();
				})
				.then(rows => {
					if (Array.isArray(rows)) {
						setStatusUnavailable(false);
						setStatusRows(rows);
					}
				})
				.catch(() => setStatusUnavailable(true)),
		[]
	);

	useEffect(() => {
		DirectApi.Fetch_Raw('xero/organizations')
			.then(result => (result.status === 200 ? result.json() : null))
			.then(response => {
				const orgs = (response && response.orgs) || [];
				setOrganisations(orgs);
				if (orgs.length > 0) {
					setSelectedTenantId(orgs[0].tenantId);
				}
			})
			.catch(() => setOrganisations([]));
		loadStatus();
	}, [loadStatus]);

	// Nothing to import into. The page already offers Add directly above this section.
	if (organisations.length === 0) {
		return null;
	}

	const statusFor = configType =>
		statusRows.find(row => row.configGroup === configType.configGroup && row.organisationExternalId === selectedTenantId);

	/**
	 * What a row says, most recent knowledge first.
	 *
	 * The run's own outcome outranks the stored row: the import lambda returns non-2xx without
	 * writing any status row for an expired token, a missing tenant, a rate limit or a timeout, so
	 * relying on the row alone would leave yesterday's success on screen after today's failure -
	 * exactly what AC4 forbids.
	 */
	const describe = configType => {
		if (runningKeys.includes(configType.key)) {
			return formatMessage({id: 'settings.app_catalog.xero.config_importing'});
		}
		const failedHere = failures.some(failure => failure.tenantId === selectedTenantId && failure.key === configType.key);
		if (failedHere) {
			return formatMessage({id: 'settings.app_catalog.xero.config_import_failed'});
		}
		if (statusUnavailable) {
			return formatMessage({id: 'settings.app_catalog.xero.config_status_unavailable'});
		}

		const row = statusFor(configType);
		if (!row) {
			return formatMessage({id: 'settings.app_catalog.xero.config_never_imported'});
		}
		if (row.status !== 'SUCCESS') {
			return formatMessage({id: 'settings.app_catalog.xero.config_import_failed'});
		}

		const when = new Date(row.lastImportedAt);
		return formatMessage(
			{id: 'settings.app_catalog.xero.config_last_imported'},
			{
				// react-intl 3.x drops dateStyle from its options whitelist without erroring, so
				// the parts are named explicitly.
				timestamp: `${formatDate(when, {day: 'numeric', month: 'short', year: 'numeric'})}, ${formatTime(when)}`,
			}
		);
	};

	const toggle = key =>
		setSelectedKeys(current => (current.includes(key) ? current.filter(k => k !== key) : [...current, key]));

	const succeeded = async response => {
		if (!response || response.status !== 200) {
			return false;
		}
		try {
			const body = await response.json();
			// The lambda reports per organisation; anything other than SUCCESS is a failure even
			// though the HTTP call itself worked.
			//
			// An absent or empty results array is a failure too, not a pass. `[].every()` is
			// true, so treating a body we do not recognise as "no failures" would report a
			// successful import on a response that never said anything succeeded.
			const results = body && Array.isArray(body.results) ? body.results : null;
			return results !== null && results.length > 0 && results.every(result => result.status === 'SUCCESS');
		} catch (e) {
			return false;
		}
	};

	const runImport = async () => {
		const chosen = CONFIG_TYPES.filter(type => selectedKeys.includes(type.key));
		const tenantId = selectedTenantId;
		setRunningKeys(chosen.map(type => type.key));
		// Clear only the failures being re-run: this organisation AND a ticked type. Filtering on
		// tenantId alone also wiped types that were not ticked and are still genuinely failed -
		// and because describe() reads failures before the status row, such a type falls back to
		// whatever the server has, which is "Never imported" whenever the POST never reached the
		// lambda (network error, the flag gate's 403, or a throw before recordImport) and so no
		// FAILED row was ever stored.
		setFailures(current =>
			current.filter(failure => failure.tenantId !== tenantId || !chosen.some(type => type.key === failure.key))
		);

		const failedNow = [];
		try {
			// One POST per ticked type, sequentially: each has its own endpoint and its own status
			// row, so one failing leaves the others' timestamps correct.
			for (const configType of chosen) {
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				let response = null;
				try {
					response = await DirectApi.Fetch_Raw_With_Init(configType.importPath, {
						method: 'POST',
						credentials: 'include',
						headers,
						body: JSON.stringify({tenantId}),
					});
				} catch (e) {
					response = null;
				}
				if (!(await succeeded(response))) {
					failedNow.push({tenantId, key: configType.key});
				}
			}

			await loadStatus();
			setFailures(current => [...current, ...failedNow]);
			/*
			 * The export modal caches accounts, products and tracking categories per organisation
			 * for the life of the page, and the invoicing page prefetches into that cache on every
			 * render. Both live in the same SPA session as this one, so without this an admin who
			 * imports here and then opens an invoice sees the list as it was BEFORE the import -
			 * every product with no defaults, until a full reload. Cleared even when a type failed,
			 * because a partial import still changed what is stored.
			 */
			clearXeroLineConfigCache();
		} finally {
			// By construction rather than by audit: anything throwing between here and the resets
			// would otherwise leave a row stuck on "Importing..." and the button disabled until a
			// page reload, with no way back.
			setRunningKeys([]);
			setSelectedKeys([]);
		}
	};

	const importing = runningKeys.length > 0;

	// A tenant Forecast can no longer reach is exactly the one the organisation list offers a
	// Reconnect button for. Importing against it cannot succeed - the lambda has no usable token -
	// so the controls are disabled rather than letting the run fail and land a FAILED row that
	// says nothing the page has not already been told.
	const selectedOrganisation = organisations.find(org => org.tenantId === selectedTenantId);
	const reconnectRequired = Boolean(selectedOrganisation && selectedOrganisation.inaccessible);
	const controlsDisabled = importing || reconnectRequired;

	return (
		<ConfigPageCard className="xero-config-page-card" data-cy="xero-config-import-section">
			<Heading size="3" as="h2">
				{formatMessage({id: 'settings.app_catalog.xero.config_import_title'})}
			</Heading>
			<Description>
				<Text size="2">{formatMessage({id: 'settings.app_catalog.xero.config_import_description'})}</Text>
			</Description>

			<OrganizationField>
				<SubHeading size="2" id={ORGANIZATION_LABEL_ID}>
					{formatMessage({id: 'common.organization'})}
				</SubHeading>
				{/* Locked while a run is in flight: the POST carries the tenantId chosen at submit
				    time, so changing it mid-run would show the new organisation's status against the
				    old organisation's import. A null key is ignored: the legacy dropdown was not
				    clearable, so the selection can never be emptied. */}
				<ComboBox
					aria-labelledby={ORGANIZATION_LABEL_ID}
					defaultItems={organisations.map(org => ({id: org.tenantId, name: org.organization}))}
					value={selectedTenantId}
					onChange={key => key && setSelectedTenantId(key)}
					disabled={importing}
					// disabled only styles the input; isDisabled also locks the list's trigger button.
					isDisabled={importing}
					width={ComboBox.WIDTH.FLEX}
					data-cy="xero-config-organization"
				>
					{item => (
						<ComboBox.Item key={item.id} textValue={item.name}>
							<Text>{item.name}</Text>
						</ComboBox.Item>
					)}
				</ComboBox>
			</OrganizationField>

			<Rows>
				{CONFIG_TYPES.map(configType => {
					const label = formatMessage({id: configType.labelId});
					return (
						<Row key={configType.key}>
							{/* aria-label overrides the component's built-in "Select row" name. */}
							<Checkbox
								checked={selectedKeys.includes(configType.key)}
								onChange={() => toggle(configType.key)}
								labelText={label}
								aria-label={label}
								aria-describedby={statusIdFor(configType)}
								notBoldLabel
								disabled={controlsDisabled}
								data-cy={`xero-config-${configType.key}`}
							/>
							<Detail size="1" color="medium" noWrap id={statusIdFor(configType)}>
								{describe(configType)}
							</Detail>
						</Row>
					);
				})}
			</Rows>

			<Actions>
				{reconnectRequired ? (
					<Text size="2">{formatMessage({id: 'settings.app_catalog.xero.config_reconnect_required'})}</Text>
				) : (
					<span />
				)}
				<Button
					emphasis={EMPHASIS.HIGH}
					disabled={controlsDisabled || selectedKeys.length === 0}
					onPress={runImport}
					data-cy="xero-config-import"
				>
					{formatMessage({id: 'settings.app_catalog.xero.config_import_button'})}
				</Button>
			</Actions>
		</ConfigPageCard>
	);
};

export default XeroConfigurationImport;
